import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';
import { splitTitle, pluralize } from '../../../utils/formatting';
import { getNewClubPath, isActive as isClubActive } from '../../../utils/clubs';
import PopOverMenu from '../../PopOverMenu';
import AddPhoto from '../../PhotoUpload/AddPhoto';
import Img from '../../Img';
import TabBar from '../../TabBar';
import { removeBannerPhoto } from '../../../actions/event-actions';
import RegistrationCta from './RegistrationCta';
import PaidHitRegistrationButton from './PaidHitRegistrationButton';
import DivisionList from './DivisionList';
import Popup from '../../Popup';

import styles from '../../../pages/EventProfiles/event.scss';
import { EventTypes, getPaidHitterExperience, isEventType } from '../../../utils/events';
import AddEventToCalendar from '../../AddToCalander';
import { useStatsig } from '../../hooks/useStatsig';
import config from '../../../../config';
import StatsigLink from '../../Links/StatsigLink';

const _ = { get };

const EventProfileHeader = (props) => {
    const { isCurrentMemberAdmin, collapsed, bannerImage, clubVerificationFee, tabs, previewOnly } = props;

    const { auth, events, coreUI } = useSelector((state) => state);
    const dispatch = useDispatch();
    const { featureGates } = useStatsig();

    const [showSafeSportPopup, setShowSafeSportPopup] = useState(false);
    const photoInputRef = useRef(null);
    const tabBarRef = useRef(null);

    const { data: event } = events.currentEvent;
    const bannerImageSrc = `/v1/tms/events/${bannerImage}`;

    let showPlayers = true;
    if (!isCurrentMemberAdmin && (event && !event.playerVisibility)) {
        showPlayers = false;
    }

    const userIsPowered = auth && auth.user && auth.user.isPowered;
    const isFlexLeagueEvent = isEventType(event, EventTypes.FLEX_LEAGUE);
    const isPaidHitEvent = isEventType(event, EventTypes.PAID_HIT);
    const isPaidHitterRegistered = event?.registeredMemberIds?.includes(event?.eventDirectors[0].memberId);
    const showAddAttachment = isCurrentMemberAdmin && !previewOnly && event && !isPaidHitEvent;
    const showAddPlayer = isCurrentMemberAdmin && !previewOnly && event?.published && event.canAddPlayer && !isPaidHitEvent;
    const showExportPlayers = isCurrentMemberAdmin && !previewOnly && event?.published && event.registeredCount > 0 && !isPaidHitEvent;
    const showMessagePlayers = isCurrentMemberAdmin && !previewOnly && event?.registeredCount > 0 && !isPaidHitEvent;
    const paidHitsData = event.paidHits;
    const playerExperienceTypesObject = coreUI?.types.playerExperienceTypes;
    const coachExperienceTypesObject = coreUI?.types.coachingExperienceTypes;
    const clubTierTypeId = event?.club?.tierTypeId;
    const newClubPath = getNewClubPath(clubTierTypeId);

    const setTabBarRef = (node) => {
        tabBarRef.current = node;
    };

    const handlePhotoPopoverClick = (item) => {
        if (!props.previewOnly) {
            if (item.value === 'remove_banner') {
                dispatch(removeBannerPhoto(event?.id));
            } else if (item.value === 'upload_banner' && photoInputRef.current) {
                photoInputRef.current.click();
            }
        }
    };

    const renderDivisionEnvironmentsAndSurfaces = () => {
        const environmentsToRender = event.eventDivisions.reduce((acc, curr) => {
            curr.environments.forEach(env => {
                if (acc.every(envType => envType !== env.value)) {
                    acc.push(env.value);
                }
            });
            return acc;
        }, []);

        const surfacesToRender = event.eventDivisions.reduce((acc, curr) => {
            curr.surfaces.forEach(surface => {
                if (acc.every(surfaceType => surfaceType !== surface.value)) {
                    acc.push(surface.value);
                }
            });

            return acc;
        }, []);

        return (
            <>
                {environmentsToRender.length > 0 && (
                    <>
                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                        <br className="d-block d-md-none" />
                        <span>
                            {environmentsToRender.join(', ')}
                        </span>
                    </>
                )}
                {surfacesToRender.length > 0 && (
                    <>
                        <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                        <br className="d-block d-md-none" />
                        <span>
                            {surfacesToRender.join(', ')}
                        </span>
                    </>
                )}
            </>
        );
    };

    const getAddPlayerText = () => {
        return isFlexLeagueEvent ? 'sub' : 'player';
    };

    const shouldShowAddToCalendar = () => {
        return auth.user && event.registered;
    };

    return (
        <>
            <div className={`${styles.section} bottom-border header`}>
                <div className={`container pb0`}>
                    <div className="row">
                        {showAddPlayer && (
                            <div
                                className="col-12 mb8 cursor-pointer d-block d-md-none"
                                onClick={() => {
                                    props.togglePlayerRegisterPopUp();
                                }}
                            >
                                <span><i className="mr8 uts uts-close transform-45" />Add a {getAddPlayerText()}</span>
                            </div>
                        )}
                        {showExportPlayers && (
                            <div
                                className="col-12 mb8 cursor-pointer d-block d-md-none"
                                onClick={() => {
                                    props.exportRegistrants();
                                }}
                            >
                                <span><i className="mr8 uts uts-export" />Export Players</span>
                            </div>
                        )}
                        {showMessagePlayers && (
                            <div
                                className="col-12 mb8 cursor-pointer d-block d-md-none"
                                onClick={() => {
                                    props.toggleMessagePopUp();
                                }}
                            >
                                <span><i className="mr8 uts uts-message" />Message Players</span>
                            </div>
                        )}
                        <div className="col-md-12">
                            <div className="d-flex flex-row align-items-center">
                                {event && event.club.name && !isPaidHitEvent && (
                                    <p className="text-highlight2 text-teal flex-grow">
                                        <StatsigLink
                                            gate={featureGates.CLUB_LINKS_REDIRECT}
                                            enabledTo={`${config.nextAppHost}/${newClubPath}/${event.club.id}`}
                                            disabledTo={`/clubs/${event.club.id}`}
                                        >
                                            {event.club.name}
                                        </StatsigLink>
                                    </p>
                                )}
                                {showAddAttachment && (
                                    <div
                                        className="mb8 ml16 cursor-pointer d-none d-md-block"
                                        onClick={() => {
                                            props.toggleUploadAttachmentPopUp();
                                        }}
                                    >
                                        <span><i className="mr8 uts uts-close transform-45" />Add Attachment</span>
                                    </div>
                                )}
                                {showAddPlayer && (
                                    <div
                                        className="mb8 ml16 cursor-pointer d-none d-md-block"
                                        onClick={() => {
                                            props.togglePlayerRegisterPopUp();
                                        }}
                                    >
                                        <span><i className="mr8 uts uts-close transform-45" />Add a {getAddPlayerText()}</span>
                                    </div>
                                )}
                                {showExportPlayers && (
                                    <div
                                        className="mb8 ml16 cursor-pointer d-none d-md-block"
                                        onClick={() => {
                                            props.exportRegistrants();
                                        }}
                                    >
                                        <span><i className="mr8 uts uts-export" />Export Players</span>
                                    </div>
                                )}
                                {showMessagePlayers && (
                                    <div
                                        className="mb8 ml16 cursor-pointer d-none d-md-block"
                                        onClick={() => {
                                            props.toggleMessagePopUp();
                                        }}
                                    >
                                        <span><i className="mr8 uts uts-message" />Message Players</span>
                                    </div>
                                )}
                                {!previewOnly && (
                                    <div
                                        className="ml16 mb8 d-none d-md-block cursor-pointer"
                                        onClick={() => {
                                            props.showShareUrlPopup();
                                        }}
                                    >
                                        <span><i className="uts uts-share pr-2" />Share</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-8">
                            <div className="event-title">
                                <h2
                                    className={`text-semi mb8`}
                                >
                                    {splitTitle(event.name).firstPart}
                                    <span className={`${event.utrType.id === 2 ? 'verified' : 'unverified'}`}>
                                        {splitTitle(event.name).lastPart}
                                        <span className={`${event?.hasPrizeMoney ? 'prizeEventIcon fal fa-usd-circle' : 'noPrizeMoney'}`}>
                                            {event?.hasSafeSportCertifiedOrganizer && (
                                                <span
                                                    className="safeSportCertified"
                                                    onClick={() => setShowSafeSportPopup(true)}
                                                />
                                            )}
                                        </span>
                                    </span>
                                </h2>
                                {
                                    isPaidHitEvent && (
                                        <h6 className="subtitle text-medium text-black text-uppercase">
                                            <span>{getPaidHitterExperience(paidHitsData, playerExperienceTypesObject, coachExperienceTypesObject, userIsPowered, isCurrentMemberAdmin)}</span>
                                        </h6>
                                    )
                                }
                                <h6 className="subtitle text-medium text-gray text-uppercase mb24">
                                    <span>{event?.eventSchedule.eventDatesShort}</span>
                                    <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                    <br className="d-block d-md-none" />
                                    <span>{_.get(event, 'eventLocations', []).length && (event.eventLocations[0].placeName || event.eventLocations[0].display)} </span>
                                    <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                    <br className="d-block d-md-none" />
                                    <span>{event?.priceRange}</span>

                                    {event?.hasPrizeMoney && event.eventTours.length > 0 && (
                                        <>
                                            <br />
                                            <span>{event.prizeMoneyText}</span>
                                            <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                            <br className="d-block d-md-none" />
                                            <span>
                                                {event.eventTours.map((tour, i) => (
                                                    <span key={`et${i}`}>{tour.name}{i < event.eventTours.length - 1 ? ', ' : ''}</span>
                                                ))}
                                            </span>
                                        </>
                                    )}
                                    {event?.hasPrizeMoney && event.eventTours.length <= 0 && (
                                        <>
                                            <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                            <br className="d-block d-md-none" />
                                            <span>{event.prizeMoneyText}</span>
                                        </>
                                    )}
                                    {renderDivisionEnvironmentsAndSurfaces()}
                                    {event && event.eventTours.length > 0 && !event.hasPrizeMoney && (
                                        <>
                                            <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                            <br className="d-block d-md-none" />
                                            <span>
                                                {event.eventTours.map((tour, i) => (
                                                    <span key={`et${i}`}>{tour.name}{i < event.eventTours.length - 1 ? ', ' : ''}</span>
                                                ))}
                                            </span>
                                        </>
                                    )}
                                    {event && event.eventType.id === 14 && event.eventDivisions[0]?.utrRange && (
                                        <>
                                            <span className={`d-none d-md-inline-block middleDot`}>&nbsp;</span>
                                            <br className="d-block d-md-none" />
                                            <span>
                                                UTR {event.eventDivisions[0]?.utrRange}
                                            </span>
                                        </>
                                    )}
                                    <br className="d-block d-md-none" />
                                    <span
                                        className="d-block d-md-none cursor-pointer"
                                        onClick={() => {
                                            props.showShareUrlPopup();
                                        }}
                                    >
                                        <span><i className="uts uts-share" />Share</span>
                                    </span>
                                    {shouldShowAddToCalendar() && <AddEventToCalendar uiElement={'link'} eventDetails={event} loggedInUserId={auth?.user?.id} />}
                                </h6>
                            </div>
                        </div>

                        <div className={`col-md-4 divisions header-title ${collapsed ? 'd-none' : ''}`}>
                            <div className="divisions-title-wrapper d-none d-sm-block">
                                <div className="divisions-title">
                                    <p className="text-highlight2 text-uppercase mb0">
                                        {`${event.eventDivisions.length} ${pluralize('Division', event.eventDivisions.length)}`}
                                    </p>
                                    <div className="divisions-title-bg" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className={`col-md-8 ${!bannerImage && !isCurrentMemberAdmin ? 'd-none d-md-block' : ''}`}>
                            <div className={`position-relative ${collapsed ? 'd-none' : ''}`}>
                                <div className={`event-image`}>
                                    {bannerImage && (
                                        <Img
                                            src={bannerImageSrc}
                                            srcSet={`
                                                ${bannerImageSrc}?size=1x&type=banner 1x,
                                                ${bannerImageSrc}?size=2x&type=banner 2x,
                                                ${bannerImageSrc}?size=3x&type=banner 3x`}
                                        />
                                    )}
                                    {!bannerImage && (
                                        <div className="w-100 h-100" />
                                    )}
                                    {(isCurrentMemberAdmin) && (
                                        <div className={`edit-banner`}>
                                            <PopOverMenu
                                                menuItems={[
                                                    ...([{ value: 'upload_banner', label: 'Upload' }]),
                                                    ...bannerImage ? ([{ value: 'remove_banner', label: 'Remove' }]) : [],
                                                ]}
                                                onSelected={handlePhotoPopoverClick}
                                            >
                                                <button
                                                    type="button"
                                                    className="btn btn-lg btn-dark button-float"
                                                >
                                                    Edit Banner <i className="uts uts-expand mr0" />
                                                </button>
                                            </PopOverMenu>
                                            <AddPhoto
                                                maxSize={2048}
                                                uploadPhoto={props.handleUploadPhoto}
                                                uploadStart={props.handleUploadPhotoStart}
                                                text={'Upload'}
                                                // TODO: refactor AddPhoto component to change the style prop name to something else "styleType".
                                                // eslint-disable-next-line
                                                style={2}
                                                inputRef={photoInputRef}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`col-md-4 ${collapsed ? 'd-none' : ''}`}>
                            <DivisionList
                                showPlayers={showPlayers}
                                clubVerificationFee={clubVerificationFee}
                                isPaidHitEvent={isPaidHitEvent}
                                isPaidHitterRegistered={isPaidHitterRegistered}
                                isCurrentMemberAdmin={isCurrentMemberAdmin}
                                handleDivisionClick={props.handleDivisionClick}
                            />
                        </div>
                    </div>
                    <div className={`row`}>
                        <div className={`col-12 col-md-4 offset-md-8 ${collapsed ? 'd-none' : ''}`}>
                            {/* Register button should be hidden if club is inactive i.e. suspended */}
                            {isClubActive(event.club) && (
                                isPaidHitEvent ? (
                                    <PaidHitRegistrationButton
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        handleRegClick={props.handleRegClick}
                                    />
                                ) : (
                                    <RegistrationCta
                                        isCurrentMemberAdmin={isCurrentMemberAdmin}
                                        handleRegClick={props.handleRegClick}
                                        handleTabClick={props.handleTabClick}
                                    />
                                )
                            )}
                        </div>
                    </div>
                    <div className={`row ${collapsed ? 'd-none' : ''}`}>
                        <div className="col btn-tabs">
                            <TabBar
                                tabs={tabs}
                                handleTabClick={props.handleTabClick}
                                ref={setTabBarRef}
                                showLabels={false}
                                eventName="Event Tab Clicked"
                                transparent={true}
                                noContainer={true}
                                data-testid="event-profile.header-legacy.tab-bar"
                                noBorder={true}
                                clickCategory="Event Profile"
                                clickLocation="Header Tabs"
                            />
                        </div>
                    </div>
                </div>
            </div>
            {showSafeSportPopup && (
                <Popup
                    className="popup-md"
                    onCloseClick={() => {
                        setShowSafeSportPopup(false);
                    }}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                    }}
                >
                    <div className="popup-section p24 text-dark">
                        <p>
                            This organizer has completed training with the U.S. Center for SafeSport. Learn more at <a href={'https://uscenterforsafesport.org/'} rel="noopener noreferrer" target="_blank">uscenterforsafesport.org</a>.
                        </p>
                    </div>
                </Popup>
            )}
        </>
    );
};

export default withRouter(EventProfileHeader);
